import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { controlCorpusPlow, fetchPipelineOps, rebuildCorpusStats } from '../services/pipelineOpsApi';
import './ResearchPage.css';
import './AdminPipelineOpsPage.css';

const REFRESH_MS = 15000;

function num(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return Number(v).toLocaleString();
}

function pct(part, whole) {
  if (whole == null || part == null || !Number(whole)) return '—';
  return `${((100 * Number(part)) / Number(whole)).toFixed(1)}%`;
}

function fmtTime(v) {
  if (!v) return '—';
  try {
    let s = String(v).trim();
    // Instant → ...Z. Legacy LocalDateTime has no zone — treat as UTC so local display is correct.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
      s = `${s}Z`;
    }
    return new Date(s).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'medium',
    });
  } catch {
    return String(v);
  }
}

function failRate(failed, processed) {
  const p = Number(processed);
  const f = Number(failed) || 0;
  if (!p) return null;
  return (100 * f) / p;
}

function Metric({ label, value, hint, tone }) {
  return (
    <div className={`ops-metric${tone ? ` ops-metric--${tone}` : ''}`}>
      <div className="ops-metric-label">{label}</div>
      <div className="ops-metric-value">{value}</div>
      {hint ? <div className="ops-metric-hint">{hint}</div> : null}
    </div>
  );
}

function ProgressBar({ done, total, label }) {
  const d = Number(done) || 0;
  const t = Number(total) || 0;
  if (!t) return null;
  const pctVal = Math.min(100, (100 * d) / t);
  // Keep a visible nub once any work has started (0.04% of 120k is invisible otherwise).
  const width = d > 0 ? Math.max(pctVal, 1.2) : 0;
  return (
    <div className="ops-progress-block" aria-label={label || 'Progress'}>
      <div className="ops-progress-meta">
        <span>
          {num(d)} / {num(t)}
        </span>
        <span>{pctVal.toFixed(2)}%</span>
      </div>
      <div className="ops-progress">
        <div className="ops-progress-fill" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

/** Stacked pass / fail / skip for a batch or cumulative run. */
function OutcomeStrip({ pushed, failed, skipped, processed, articleFailed, pipelineFailed }) {
  const p = Number(pushed) || 0;
  const art = articleFailed != null ? Number(articleFailed) || 0 : null;
  const pipe = pipelineFailed != null ? Number(pipelineFailed) || 0 : null;
  const f = art != null ? art + (pipe || 0) : Number(failed) || 0;
  const s = Number(skipped) || 0;
  const total = Number(processed) || p + f + s;
  if (!total) {
    return <p className="ops-empty">No batch outcomes yet</p>;
  }
  const splitFails = art != null;
  const segments = splitFails
    ? [
        { key: 'pass', n: p, cls: 'ops-outcome--pass', label: 'Pass' },
        { key: 'article', n: art, cls: 'ops-outcome--article', label: 'Article' },
        { key: 'pipe', n: pipe || 0, cls: 'ops-outcome--pipe', label: 'Pipe' },
        { key: 'skip', n: s, cls: 'ops-outcome--skip', label: 'Skip' },
      ]
    : [
        { key: 'pass', n: p, cls: 'ops-outcome--pass', label: 'Pass' },
        { key: 'fail', n: f, cls: 'ops-outcome--fail', label: 'Fail' },
        { key: 'skip', n: s, cls: 'ops-outcome--skip', label: 'Skip' },
      ];
  const visible = segments.filter((x) => x.n > 0);
  const title = splitFails
    ? `pass ${p} · article ${art} · pipe ${pipe || 0} · skip ${s}`
    : `pass ${p} · fail ${f} · skip ${s}`;
  return (
    <div className="ops-outcome">
      <div className="ops-outcome-track" title={title}>
        {visible.map((seg) => (
          <div
            key={seg.key}
            className={`ops-outcome-seg ${seg.cls}`}
            style={{ flexGrow: seg.n, flexBasis: 0 }}
          />
        ))}
      </div>
      <div className="ops-outcome-legend">
        {segments.map((seg) => (
          <span key={seg.key} className={`ops-outcome-leg ops-outcome-leg--${seg.key}`}>
            {seg.label} {num(seg.n)}
          </span>
        ))}
      </div>
    </div>
  );
}

function BarChart({ rows, max }) {
  const peak = max || Math.max(1, ...rows.map((r) => Number(r.count) || 0));
  return (
    <div className="ops-bars" role="list">
      {rows.map((row) => {
        const count = Number(row.count) || 0;
        const width = Math.max(count > 0 ? 2 : 0, Math.round((100 * count) / peak));
        return (
          <div className="ops-bar-row" key={row.label} role="listitem">
            <div className="ops-bar-label" title={row.label}>
              {row.label}
            </div>
            <div className="ops-bar-track">
              <div className="ops-bar-fill" style={{ width: `${width}%` }} />
            </div>
            <div className="ops-bar-count">{num(count)}</div>
          </div>
        );
      })}
    </div>
  );
}

function MixChips({ mix }) {
  const entries = Object.entries(mix || {});
  if (!entries.length) {
    return <p className="ops-empty">No data yet</p>;
  }
  const total = entries.reduce((s, [, c]) => s + Number(c || 0), 0) || 1;
  return (
    <div className="ops-chips">
      {entries.map(([k, c]) => (
        <span className="ops-chip" key={k}>
          <strong>{k}</strong> {num(c)} · {((100 * Number(c)) / total).toFixed(0)}%
        </span>
      ))}
    </div>
  );
}

function stateTone(state) {
  const s = String(state || '').toUpperCase();
  if (s === 'RUNNING') return 'good';
  if (s === 'PAUSED') return 'warn';
  if (s === 'FAILED' || s === 'ERROR') return 'bad';
  return undefined;
}

/** Tick every second from last DGX push so the pill feels live; resets when push age jumps down. */
function useDgxAgeSeconds(lastPushAt) {
  const [age, setAge] = useState(null);
  useEffect(() => {
    if (!lastPushAt) {
      setAge(null);
      return undefined;
    }
    const base = new Date(lastPushAt).getTime();
    if (Number.isNaN(base)) {
      setAge(null);
      return undefined;
    }
    const tick = () => setAge(Math.max(0, Math.floor((Date.now() - base) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [lastPushAt]);
  return age;
}

const AdminPipelineOpsPage = () => {
  useDocumentTitle('Admin · Pipeline Ops');
  const { user, authReady, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [runId, setRunId] = useState('');
  const [runIdDraft, setRunIdDraft] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastFetchAt, setLastFetchAt] = useState(null);
  const [rebuildBusy, setRebuildBusy] = useState(false);

  const reload = useCallback(async () => {
    const snapshot = await fetchPipelineOps(runId || undefined);
    setData(snapshot);
    setLastFetchAt(new Date());
    setError(null);
  }, [runId]);

  useEffect(() => {
    if (!authReady) return;
    if (!isAuthenticated || user?.principalName !== 'admin') {
      navigate('/login', { replace: true });
      return;
    }
    reload().catch((e) => setError(e.message));
  }, [authReady, isAuthenticated, user, navigate, reload]);

  useEffect(() => {
    if (!authReady || !autoRefresh) return undefined;
    if (!isAuthenticated || user?.principalName !== 'admin') return undefined;
    const id = window.setInterval(() => {
      reload().catch((e) => setError(e.message));
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [authReady, autoRefresh, isAuthenticated, user, reload]);

  const dgx = data?.dgx;
  const liveAgeSec = useDgxAgeSeconds(dgx?.lastPushAt);
  const status = dgx?.status || {};
  const plow = status.corpusPlow || {};
  const checkpoint = status.backfillCheckpoint || {};
  const harvestCycle = status.harvestCycle || {};
  const lastHarvest = status.lastHarvestResult || {};
  const corpus = data?.corpus || {};
  const phaseB = status.phaseBEntityExtraction || {};

  const tierRows = useMemo(() => {
    const by = corpus.byTier || {};
    return Object.entries(by).map(([label, count]) => ({ label, count }));
  }, [corpus.byTier]);

  const categoryTier = useMemo(() => {
    const by = corpus.categoryByTier || {};
    return Object.entries(by).map(([label, count]) => ({ label, count }));
  }, [corpus.categoryByTier]);

  const gapRows = useMemo(() => corpus.gapHistogram || [], [corpus.gapHistogram]);

  const backfillFailPct = failRate(checkpoint.totalFailed, checkpoint.totalProcessed);
  const harvestLive = Boolean(status.harvestCycleInProgress);
  const harvestSplit =
    (harvestLive ? harvestCycle : lastHarvest).articlesArticleFailed != null ||
    (harvestLive ? harvestCycle : lastHarvest).articlesPipelineFailed != null;
  const harvestPushed = harvestLive
    ? Number(harvestCycle.articlesPushed) || 0
    : plow.lastHarvestPushed != null
      ? Number(plow.lastHarvestPushed)
      : Number(lastHarvest.articlesPushed) || 0;
  const harvestFetched = harvestLive
    ? Number(harvestCycle.articlesFetched) || 0
    : plow.lastHarvestFetched != null
      ? Number(plow.lastHarvestFetched)
      : Number(lastHarvest.articlesFetched) || 0;
  const harvestArticleFailed = harvestSplit
    ? Number(
        harvestLive ? harvestCycle.articlesArticleFailed : lastHarvest.articlesArticleFailed
      ) || 0
    : 0;
  const harvestPipelineFailed = harvestSplit
    ? Number(
        harvestLive ? harvestCycle.articlesPipelineFailed : lastHarvest.articlesPipelineFailed
      ) || 0
    : 0;
  const harvestFailed = harvestSplit
    ? harvestArticleFailed + harvestPipelineFailed
    : Number(harvestLive ? harvestCycle.articlesFailed : lastHarvest.articlesFailed) || 0;
  const harvestSkipped = harvestLive
    ? Number(harvestCycle.articlesSkipped) || 0
    : Number(lastHarvest.articlesSkipped) || 0;
  const harvestRssFailed =
    Number(harvestLive ? harvestCycle.feedsRssFailed : lastHarvest.feedsRssFailed) || 0;
  const harvestRssOk =
    Number(harvestLive ? harvestCycle.feedsRssOk : lastHarvest.feedsRssOk) || 0;
  const harvestFailPct = failRate(
    harvestFailed,
    harvestFetched || harvestPushed + harvestFailed + harvestSkipped
  );
  const harvestArticleFailPct = failRate(
    harvestArticleFailed,
    harvestFetched || harvestPushed + harvestFailed + harvestSkipped
  );

  const plowState = String(plow.state || 'IDLE').toUpperCase();
  const plowActive = ['RUNNING', 'PAUSED', 'STOPPING'].includes(plowState);
  const catalogTotal =
    Number(checkpoint.totalTarget) ||
    Number(checkpoint.catalogSize) ||
    Number(checkpoint.candidates) ||
    0;
  const processed = Number(checkpoint.totalProcessed) || 0;
  const ratePerHour = checkpoint.ratePerHour != null ? Number(checkpoint.ratePerHour) : null;
  const ratePerMin = ratePerHour != null ? ratePerHour / 60 : null;

  const batchSize = Number(plow.batchSize) || 0;
  const lastBatchProcessed = plow.lastBatchProcessed != null ? Number(plow.lastBatchProcessed) : null;
  const lastBatchPushed = plow.lastBatchPushed != null ? Number(plow.lastBatchPushed) : null;

  const onApplyRunId = (e) => {
    e.preventDefault();
    setRunId(runIdDraft.trim());
  };

  const onPlowAction = async (action) => {
    if (!window.confirm(`Queue corpus-plow ${action} for DGX? (applied on next poll ~5s)`)) return;
    setBusy(true);
    try {
      await controlCorpusPlow(action);
      await reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onRebuildCorpus = async () => {
    setRebuildBusy(true);
    setError(null);
    try {
      const corpusStats = await rebuildCorpusStats();
      setData((prev) => (prev ? { ...prev, corpus: corpusStats } : { corpus: corpusStats }));
      setLastFetchAt(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setRebuildBusy(false);
    }
  };

  const dgxPillClass = !dgx?.lastPushAt
    ? 'muted'
    : dgx.reachable
      ? 'good'
      : dgx.configured
        ? 'bad'
        : 'muted';
  const dgxPillText = !dgx?.lastPushAt
    ? 'Awaiting DGX push'
    : dgx.reachable
      ? `DGX live · ${liveAgeSec != null ? liveAgeSec : '—'}s`
      : `DGX stale · ${liveAgeSec != null ? liveAgeSec : num(dgx.ageSeconds)}s`;

  if (!authReady) {
    return (
      <div className="research-page">
        <div className="container">
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`research-page admin-ops-page${busy ? ' admin-ops-busy' : ''}`}>
      <div className="container">
        <header className="admin-ops-header">
          <div>
            <h1>Pipeline Ops</h1>
            <p className="admin-ops-help">
              Corpus plow vs live harvest side-by-side — BR-05. Auto-refresh{' '}
              {autoRefresh ? `every ${REFRESH_MS / 1000}s` : 'off'}. DGX pushes status outbound (no
              VPN). High harvest &quot;fail&quot; is usually HTML 403/timeouts, not a dead plow.
            </p>
          </div>
          <nav className="admin-ops-nav">
            <Link to="/admin/feeds">Feeds</Link>
            <Link to="/admin/agents">Agents</Link>
          </nav>
        </header>

        <div className="admin-ops-toolbar">
          <form className="admin-ops-runid" onSubmit={onApplyRunId}>
            <label htmlFor="ops-runid">runId</label>
            <input
              id="ops-runid"
              type="text"
              value={runIdDraft}
              placeholder={status.resolvedRunId || plow.runId || 'ms-2.7c'}
              onChange={(e) => setRunIdDraft(e.target.value)}
            />
            <button type="submit" className="btn btn-sm btn-outline-primary">
              Apply
            </button>
          </form>
          <label className="admin-ops-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={busy}
            onClick={() => reload().catch((e) => setError(e.message))}
          >
            Refresh now
          </button>
          <span className={`ops-pill ops-pill--${dgxPillClass} admin-ops-dgx-pill`}>
            {dgxPillText}
          </span>
          <span className="admin-ops-fetched">
            {lastFetchAt ? `UI ${lastFetchAt.toLocaleTimeString()}` : '—'}
          </span>
        </div>

        {error ? <div className="alert alert-danger">{error}</div> : null}

        {!dgx?.lastPushAt ? (
          <p className="ops-empty">
            Waiting for DGX outbound status push (
            <code>POST /internal/pipeline/ops-status</code>). Mongo quality panels below still work.
          </p>
        ) : null}

        {dgx?.lastPushAt && dgx?.stale ? (
          <p className="ops-empty ops-empty--warn">
            {dgx.error || 'Status stale'} · host {dgx.sourceHost || '—'} · pending commands{' '}
            {num(dgx.pendingCommands)}
          </p>
        ) : null}

        <section className="ops-section">
          <div className="ops-section-head">
            <h2>Live operations</h2>
            <span className="ops-pill ops-pill--muted">
              {plowActive ? 'plow owns harvest interleave' : 'cron harvest when plow idle'}
            </span>
          </div>

          <div className="ops-split">
            {/* ——— PLOW (left) ——— */}
            <div className="ops-panel ops-panel--plow">
              <div className="ops-panel-head">
                <h3>Corpus plow</h3>
                <span className={`ops-pill ops-pill--${stateTone(plowState) || 'muted'}`}>
                  {plowState}
                </span>
              </div>
              <p className="ops-panel-sub ops-mono">
                {plow.runId || checkpoint.runId || '—'}
                {plow.catalog || checkpoint.catalog
                  ? ` · ${String(plow.catalog || checkpoint.catalog).split('/').pop()}`
                  : ''}
                {plow.stopReason ? ` · ${plow.stopReason}` : ''}
              </p>

              <ProgressBar
                done={processed}
                total={catalogTotal}
                label="Freeze catalog progress"
              />

              <div className="ops-metric-grid ops-metric-grid--panel">
                <Metric
                  label="Processed / catalog"
                  value={catalogTotal ? `${num(processed)} / ${num(catalogTotal)}` : num(processed)}
                  hint={`remaining ${num(checkpoint.remaining)} · phase ${String(plow.phase || 'a').toUpperCase()}`}
                />
                <Metric
                  label="Last batch"
                  value={
                    lastBatchProcessed != null
                      ? `${num(lastBatchProcessed)} / ${num(batchSize) || '—'}`
                      : batchSize
                        ? `— / ${num(batchSize)}`
                        : '—'
                  }
                  hint={
                    lastBatchPushed != null
                      ? `pushed ${num(lastBatchPushed)}${plow.lastBatchHasMore === false ? ' · catalog end' : ''}`
                      : `batchSize ${num(batchSize)}`
                  }
                />
                <Metric
                  label="Pass / fail / skip"
                  value={`${num(checkpoint.totalPushed)} / ${num(checkpoint.totalFailed)} / ${num(checkpoint.totalSkipped)}`}
                  hint={
                    backfillFailPct != null
                      ? `fail rate ${backfillFailPct.toFixed(1)}%`
                      : 'cumulative for run'
                  }
                  tone={
                    backfillFailPct != null && backfillFailPct > 5
                      ? 'bad'
                      : backfillFailPct != null && backfillFailPct > 0
                        ? 'warn'
                        : processed
                          ? 'good'
                          : undefined
                  }
                />
                <Metric
                  label="Rate"
                  value={
                    ratePerMin != null
                      ? `${ratePerMin.toFixed(1)}/min`
                      : '—'
                  }
                  hint={
                    ratePerHour != null
                      ? `${num(ratePerHour)}/hr · ETA ${checkpoint.etaHours != null ? `~${num(checkpoint.etaHours)} h` : '—'}`
                      : plowActive
                        ? 'warming up after first batch'
                        : 'idle'
                  }
                />
                <Metric
                  label="Cycles"
                  value={`${num(plow.cyclesCompleted)}${plow.maxCycles ? ` / ${plow.maxCycles}` : ''}`}
                  hint={`harvest ${num(plow.harvestCycles)} · Phase A ${num(plow.phaseABatches)}`}
                />
                <Metric
                  label="Cmd queue"
                  value={num(dgx?.pendingCommands)}
                  hint={
                    dgx?.lastAck?.action
                      ? `last ack ${dgx.lastAck.action} ${dgx.lastAck.ok === false ? 'FAIL' : 'ok'}`
                      : 'pause / resume / stop'
                  }
                  tone={dgx?.pendingCommands > 0 ? 'warn' : undefined}
                />
              </div>

              <OutcomeStrip
                pushed={checkpoint.totalPushed}
                failed={checkpoint.totalFailed}
                skipped={checkpoint.totalSkipped}
                processed={checkpoint.totalProcessed}
              />

              <dl className="ops-dl ops-dl--compact">
                <div>
                  <dt>cursor</dt>
                  <dd className="ops-mono">{checkpoint.cursor || plow.lastCursor || '—'}</dd>
                </div>
                <div>
                  <dt>updated</dt>
                  <dd>{fmtTime(checkpoint.updatedAt || plow.updatedAt)}</dd>
                </div>
                <div>
                  <dt>last error</dt>
                  <dd className={checkpoint.lastError || plow.lastError ? 'ops-bad-text' : ''}>
                    {checkpoint.lastError || plow.lastError || 'none'}
                  </dd>
                </div>
              </dl>

              <div className="ops-controls">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-warning"
                  disabled={busy}
                  onClick={() => onPlowAction('pause')}
                >
                  Queue pause
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success"
                  disabled={busy}
                  onClick={() => onPlowAction('resume')}
                >
                  Queue resume
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={busy}
                  onClick={() => onPlowAction('stop')}
                >
                  Queue stop
                </button>
              </div>
              <p className="ops-empty" style={{ marginTop: '0.5rem' }}>
                Commands apply when DGX polls (~5s). Host: {dgx?.sourceHost || '—'}
              </p>
            </div>

            {/* ——— HARVEST (right) ——— */}
            <div className="ops-panel ops-panel--harvest">
              <div className="ops-panel-head">
                <h3>Live harvest</h3>
                <span
                  className={`ops-pill ops-pill--${
                    status.harvestCycleInProgress
                      ? 'good'
                      : status.harvestEnabled
                        ? 'muted'
                        : 'bad'
                  }`}
                >
                  {status.harvestCycleInProgress
                    ? 'IN CYCLE'
                    : status.harvestEnabled
                      ? 'IDLE'
                      : 'OFF'}
                </span>
              </div>
              <p className="ops-panel-sub">
                {plowActive
                  ? 'Owned by plow interleave (cron yields)'
                  : `Cron ~${status.updateIntervalMs ? Math.round(Number(status.updateIntervalMs) / 60000) : '—'} min`}
              </p>

              <ProgressBar
                done={
                  harvestLive
                    ? harvestCycle.feedIndex
                    : harvestCycle.lastActivityKind === 'cycle_complete'
                      ? harvestCycle.feedsTotal
                      : harvestRssOk + harvestRssFailed || 0
                }
                total={
                  Number(harvestCycle.feedsTotal) ||
                  Number(status.enabledFeeds) ||
                  0
                }
                label="Feed progress"
              />

              <div className="ops-metric-grid ops-metric-grid--panel">
                <Metric
                  label="Feeds done / enabled"
                  value={
                    harvestLive
                      ? `${num(harvestCycle.feedIndex)} / ${num(harvestCycle.feedsTotal)}`
                      : Number(status.enabledFeeds)
                        ? `${num(harvestRssOk + harvestRssFailed || harvestCycle.feedsTotal)} / ${num(status.enabledFeeds)}`
                        : '—'
                  }
                  hint={
                    harvestLive
                      ? harvestCycle.currentFeedName || '—'
                      : `last ${harvestCycle.lastActivityKind || '—'}`
                  }
                />
                <Metric
                  label="Pass / article / pipe / skip"
                  value={`${num(harvestPushed)} / ${num(
                    harvestSplit ? harvestArticleFailed : harvestFailed
                  )} / ${num(harvestSplit ? harvestPipelineFailed : 0)} / ${num(harvestSkipped)}`}
                  hint={
                    harvestArticleFailPct != null
                      ? `article ${harvestArticleFailPct.toFixed(0)}% of items · fetched ${num(harvestFetched)}${
                          harvestLive ? ' · live' : ''
                        }`
                      : harvestLive
                        ? 'live this cycle'
                        : 'last completed cycle'
                  }
                  tone={
                    harvestArticleFailPct != null && harvestArticleFailPct > 40
                      ? 'warn'
                      : harvestFailPct != null && harvestFailPct > 40
                        ? 'warn'
                        : undefined
                  }
                />
                <Metric
                  label="Rss fail"
                  value={num(harvestRssFailed)}
                  hint={
                    harvestRssOk || harvestRssFailed
                      ? `rss ok ${num(harvestRssOk)} · feed XML / parse`
                      : 'feed fetch/parse fails (not article 403)'
                  }
                  tone={harvestRssFailed > 0 ? 'warn' : undefined}
                />
                <Metric
                  label="Enabled feeds"
                  value={`${num(status.enabledFeeds)} / ${num(status.totalFeeds)}`}
                  hint={`source ${status.feedSource || '—'} · buffer ${num(status.bufferDepth)}`}
                />
                <Metric
                  label="Concurrency"
                  value={num(status.harvestConcurrency)}
                  hint={`backfill ${num(status.backfillConcurrency)}`}
                />
                <Metric
                  label="Window (local)"
                  value={fmtTime(status.lastHarvestEnd)}
                  hint={`start ${fmtTime(status.lastHarvestStart)}`}
                />
              </div>

              <OutcomeStrip
                pushed={harvestPushed}
                failed={harvestFailed}
                skipped={harvestSkipped}
                articleFailed={harvestSplit ? harvestArticleFailed : undefined}
                pipelineFailed={harvestSplit ? harvestPipelineFailed : undefined}
                processed={harvestPushed + harvestFailed + harvestSkipped}
              />

              <dl className="ops-dl ops-dl--compact">
                <div>
                  <dt>interrupted</dt>
                  <dd>
                    {lastHarvest.interrupted ? lastHarvest.interruptReason || 'yes' : 'no'}
                  </dd>
                </div>
                <div>
                  <dt>activity</dt>
                  <dd className="ops-mono">
                    {harvestCycle.lastActivityKind || '—'}
                    {harvestCycle.lastActivityAt
                      ? ` · ${fmtTime(harvestCycle.lastActivityAt)}`
                      : ''}
                  </dd>
                </div>
                <div>
                  <dt>Phase B model</dt>
                  <dd className="ops-mono">{phaseB.model || '—'}</dd>
                </div>
                <div>
                  <dt>prompt</dt>
                  <dd className="ops-mono">{phaseB.promptVersion || '—'}</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>

        <section className="ops-section">
          <div className="ops-section-head">
            <h2>Corpus quality (Mongo)</h2>
            <div className="ops-section-head-actions">
              <span className="ops-pill ops-pill--muted">
                {num(corpus.totalArticles)} articles · snapshots {num(corpus.totalWithSnapshot)} ·
                gaps {num(corpus.gapCountGt0)}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={rebuildBusy}
                onClick={onRebuildCorpus}
                title="Full Mongo recount — reconciles incremental counter drift"
              >
                {rebuildBusy ? 'Rebuilding…' : 'Rebuild from scratch'}
              </button>
            </div>
          </div>
          <p className="ops-panel-sub">
            Live counters update on ingest (15s refresh). Last full rebuild:{' '}
            <span className="ops-mono">{fmtTime(corpus.rebuiltAt)}</span>
            {corpus.source ? ` · source ${corpus.source}` : ''}. Auto-reconcile every 15 min.
          </p>

          <div className="ops-metric-grid">
            <Metric
              label="Phase B queue"
              value={num(corpus.gapCountGt0)}
              hint={`gapCount > 0 · ${pct(corpus.gapCountGt0, corpus.totalWithSnapshot)} of snapshots`}
              tone={corpus.gapCountGt0 > 0 ? 'warn' : 'good'}
            />
            <Metric
              label="Clean"
              value={num(corpus.clean)}
              hint={pct(corpus.clean, corpus.totalWithSnapshot)}
              tone="good"
            />
            <Metric
              label="Repaired"
              value={num(corpus.repaired)}
              hint={pct(corpus.repaired, corpus.totalWithSnapshot)}
            />
            <Metric
              label="Parse fail"
              value={num(corpus.parseFail)}
              hint={pct(corpus.parseFail, corpus.totalWithSnapshot)}
              tone={corpus.parseFail > 0 ? 'bad' : undefined}
            />
            <Metric
              label="Length fail"
              value={num(corpus.lengthFail)}
              hint={pct(corpus.lengthFail, corpus.totalWithSnapshot)}
              tone={corpus.lengthFail > 0 ? 'warn' : undefined}
            />
            <Metric
              label="Category needs repair"
              value={num(corpus.categoryNeedsRepair)}
              hint={`${num(corpus.categoryTotalWithSnapshot)} with category snapshot`}
            />
          </div>

          <div className="ops-detail-grid ops-detail-grid--2">
            <div className="ops-card">
              <h3>Entity tier mix</h3>
              <BarChart rows={tierRows} />
            </div>
            <div className="ops-card">
              <h3>Category tier mix</h3>
              <BarChart rows={categoryTier} />
            </div>
            <div className="ops-card">
              <h3>Gap histogram (entityGapCount)</h3>
              <BarChart rows={gapRows} />
            </div>
            <div className="ops-card">
              <h3>Parse / phase mix</h3>
              <h4 className="ops-subh">Entity parseMethod</h4>
              <MixChips mix={corpus.entityParseMethodMix} />
              <h4 className="ops-subh">Entity phase</h4>
              <MixChips mix={corpus.entityPhaseMix} />
              <h4 className="ops-subh">Category parseMethod</h4>
              <MixChips mix={corpus.categoryParseMethodMix} />
            </div>
          </div>
        </section>

        <section className="ops-section">
          <div className="ops-section-head">
            <h2>Model / prompt lineage</h2>
          </div>
          <div className="ops-detail-grid ops-detail-grid--2">
            <div className="ops-card ops-card--table">
              <h3>Entity extract</h3>
              <LineageTable rows={corpus.entityLineage} />
            </div>
            <div className="ops-card ops-card--table">
              <h3>Category classify</h3>
              <LineageTable rows={corpus.categoryLineage} />
            </div>
          </div>
        </section>

        <section className="ops-section">
          <div className="ops-section-head">
            <h2>Phase B sample queue</h2>
            <span className="ops-pill ops-pill--muted">first 12 dirty candidates</span>
          </div>
          <div className="ops-card ops-card--table">
            <CandidateTable rows={data?.phaseBSample} />
          </div>
        </section>
      </div>
    </div>
  );
};

function LineageTable({ rows }) {
  if (!rows?.length) {
    return <p className="ops-empty">No lineage snapshots yet</p>;
  }
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>Prompt</th>
            <th>Phase</th>
            <th>Count</th>
            <th>Avg ms</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.modelId}|${r.promptVersion}|${r.phase}`}>
              <td className="ops-mono">{r.modelId}</td>
              <td className="ops-mono">{r.promptVersion}</td>
              <td>{r.phase}</td>
              <td>{num(r.count)}</td>
              <td>{r.avgLatencyMs != null ? num(r.avgLatencyMs) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CandidateTable({ rows }) {
  if (!rows?.length) {
    return <p className="ops-empty">Queue empty — no gap / parse_fail / length_fail rows</p>;
  }
  return (
    <div className="ops-table-wrap">
      <table className="ops-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Tier</th>
            <th>Gaps</th>
            <th>Cat tier</th>
            <th>URL</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.title || '—'}</td>
              <td>
                <span className="ops-chip ops-chip--sm">{r.entityExtractTier || '—'}</span>
              </td>
              <td>{num(r.entityGapCount)}</td>
              <td>{r.categoryExtractTier || '—'}</td>
              <td className="ops-mono ops-url">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noreferrer">
                    {r.url}
                  </a>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AdminPipelineOpsPage;
